import { Building2, MapPin, DollarSign, Clock, Users, History, Image as ImageIcon, Video } from 'lucide-react';
import { useNavigate, useNavigation } from 'react-router-dom';

export interface Lead {
  id: number;
  homeowner_id: string;
  job_title: string;
  job_description: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  lead_sub_cat:any;
  job_budget: number;
  escrow_enabled: boolean;
  job_status: 'OPEN' | 'REQUESTED' | 'ACCEPTED' | 'COMPLETED';
  created_at: string;
  media?: {
    type: 'image' | 'video';
    url: string;
    thumbnail?: string;
  }[];
  accepted_contractor?: {
    id: string;
    user_first_name: string;
    accepted_at: string;
    email: string;
    phone: string;
    rating: number;
    completed_jobs: number;
    profile_image?: string;
  };
  requested_contractors_list?: {
    id: string;
    name: string;
    email: string;
    phone: string;
    rating: number;
    completed_jobs: number;
    profile_image?: string;
    sent_at: string;
  }[];
  requested_contractors: number;
  history: HistoryEntry[];
}

export interface HistoryEntry {
  id: number;
  timestamp: string;
  action: string;
  details: string;
  user: string;
}

interface LeadCardProps {
  lead: Lead;
}

export function LeadCard({ lead }: LeadCardProps) {
  const isAccepted = lead.accepted_contractor 
  const isRequested = lead.requested_contractors > 0;
  const images = lead.media?.filter(m => m.type === 'image') || [];
  const videos = lead.media?.filter(m => m.type === 'video') || [];

  const statusColors = {
    OPEN: 'bg-gray-100 text-gray-800',
    REQUESTED: 'bg-blue-100 text-blue-800',
    ACCEPTED: 'bg-yellow-100 text-yellow-800',
    COMPLETED: 'bg-green-100 text-green-800',
  };
  const navigation =useNavigate()

  return (
    <div onClick={()=>{
      navigation(`/project-details/${lead.id}`)
    }} onKeyDown={(event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        navigation(`/project-details/${lead.id}`)
      }
    }} role="button" tabIndex={0} aria-label={`Open project ${lead.job_title}`} className="homeowner-project-card-wrap block">
      <div className={`homeowner-project-card homeowner-project-card--${lead.job_status.toLowerCase()} bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow`}>
        {/* Media Preview */}
        {lead.media && lead.media.length > 0 && (
          <div className="homeowner-project-card__media relative h-48 bg-gray-100">
            <img
              src={lead.media[0].type === 'image' ? lead.media[0].url : lead.media[0].thumbnail}
              alt={lead.job_title}
              className="w-full h-full object-cover"
            />
            {lead.media.length > 1 && (
              <div className="absolute bottom-2 right-2 bg-black/70 text-white px-2 py-1 rounded text-xs flex items-center gap-1">
                {images.length > 0 && (
                  <span className="flex items-center gap-1">
                    <ImageIcon className="w-3 h-3" />
                    {images.length}
                  </span>
                )}
                {videos.length > 0 && (
                  <span className="flex items-center gap-1 ml-2">
                    <Video className="w-3 h-3" />
                    {videos.length}
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        <div className="homeowner-project-card__content p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <h3 className="font-semibold text-lg mb-1">{lead.job_title}</h3>
              <p className="text-gray-600 text-sm line-clamp-2">{lead.job_description}</p>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ml-4 flex-shrink-0 ${statusColors[lead.job_status]}`}>
              {lead.job_status}
            </span>
          </div>

          <div className="space-y-2 mb-4">
            <div className="flex items-center text-sm text-gray-600">
              <MapPin className="w-4 h-4 mr-2 flex-shrink-0" />
              <span className="truncate">{lead.address}, {lead.city}, {lead.state} {lead.zip_code}</span>
            </div>
            <div className="flex items-center text-sm text-gray-600">
              <DollarSign className="w-4 h-4 mr-2 flex-shrink-0" />
              <span className="font-medium">${lead.job_budget.toLocaleString()}</span>
              {lead.escrow_enabled && (
                <span className="ml-2 px-2 py-0.5 bg-green-50 text-green-700 text-xs rounded">
                  Escrow
                </span>
              )}
            </div>
            <div className="flex items-center text-sm text-gray-600">
              <Clock className="w-4 h-4 mr-2 flex-shrink-0" />
              <span>{new Date(lead.created_at).toLocaleDateString()}</span>
            </div>
          </div>

      
          {lead.lead_sub_cat && lead.lead_sub_cat.length > 0 && (
            <div className="mb-4">
              <div className="flex flex-wrap gap-2">
                {lead.lead_sub_cat.slice(0, 3).map((service:any) => (
                  <span
                    key={service.id}
                    className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full border border-blue-200"
                  >
                    {service.name}
                  </span>
                ))}
                {lead.lead_sub_cat.length > 3 && (
                  <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                    +{lead.lead_sub_cat.length - 3} more
                  </span>
                )}
              </div>
            </div>
          )}
          <div className="flex items-center gap-4 pt-4 border-t border-gray-100">
            {isAccepted ? (
              <div className="flex items-center text-sm flex-1 min-w-0">
                <Users className="w-4 h-4 mr-1.5 text-green-600 flex-shrink-0" />
                <span className="text-green-600 font-medium truncate">
                  Accepted by {lead.accepted_contractor?.user_first_name}
                </span>
              </div>
            ) : isRequested ? (
              <div className="flex items-center text-sm flex-1">
                <Building2 className="w-4 h-4 mr-1.5 text-blue-600 flex-shrink-0" />
                <span className="text-blue-600 font-medium">
                  Sent to {lead.requested_contractors} {lead.requested_contractors === 1 ? 'contractor' : 'contractors'}
                </span>
              </div>
            ) : (
              <div className="flex items-center text-sm flex-1">
                <Building2 className="w-4 h-4 mr-1.5 text-gray-400 flex-shrink-0" />
                <span className="text-gray-500">
                  Available for any contractor
                </span>
              </div>
            )}
            <div className="flex items-center text-sm flex-shrink-0">
              <History className="w-4 h-4 mr-1.5 text-gray-400" />
              <span className="text-gray-500">{lead.history.length}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
